import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Calendar, Mail, CreditCard, Clock, ArrowLeft, Info, Lock } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import StripeProvider from './payment/StripeProvider';
import StripeCheckoutForm from './booking/StripeCheckoutForm';
import giftCardService from './services/giftCardServices';
import GiftCardCheckout from './booking/GiftCardCheckout'; // Import the fixed component

const GiftCardsPage = ({ isMobile }) => {
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [errors, setErrors] = useState({});
  
  // Checkout states
  const [checkoutStep, setCheckoutStep] = useState('form'); // 'form', 'payment'
  const [giftCardData, setGiftCardData] = useState(null);
  
  // Set initial sender name from auth if available
  useEffect(() => {
    if (auth.currentUser && auth.currentUser.displayName) {
      setSenderName(auth.currentUser.displayName);
    }
  }, [auth.currentUser]);
  
  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setErrors({...errors, amount: null});
  };
  
  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setSelectedAmount(0);
    setErrors({...errors, amount: null});
  };
  
  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    
    // Check amount
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    if (!amount || amount < 25) {
      newErrors.amount = 'Please select or enter a valid amount (minimum $25)';
    }
    
    // Sender name validation (if user is not logged in)
    if (!auth.currentUser && (!senderName || senderName.length < 2)) {
      newErrors.senderName = 'Please enter your name';
    }
    
    // Email validation if provided
    if (recipientEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmail)) {
        newErrors.recipientEmail = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission - move to payment step
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Get the amount
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    // Create gift card data object (but don't save it yet)
    const giftCardData = {
      amount,
      recipientName,
      recipientEmail,
      senderName: auth.currentUser ? auth.currentUser.displayName || senderName : senderName,
      message: giftMessage,
    };
    
    // Save the gift card data to state
    setGiftCardData(giftCardData);
    
    // Move to payment step
    setCheckoutStep('payment');
    
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Handle successful payment and card creation
  const handlePaymentSuccess = (createdGiftCard) => {
    // Navigate to success page
    navigate('/gift-card-success', { state: { giftCard: createdGiftCard } });
  };
  
  // Handle back button
  const handleBackFromPayment = () => {
    setCheckoutStep('form');
  };
  
  return (
    <div className="pt-6 md:pt-12 pb-16">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="inline-block bg-[#3A98B9]/10 p-3 rounded-full mb-3">
            <Gift className="h-8 w-8 text-[#3A98B9]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Gift Cards</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Give the perfect gift for any car enthusiast - a premium detailing experience they'll love.
            Our gift cards can be used for any of our services.
          </p>
        </div>
        
        {/* Main Content */}
        <div className="max-w-3xl mx-auto">
          {/* Gift Card Purchase Form or Payment */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-12">
            {checkoutStep === 'form' ? (
              <>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Purchase a Gift Card</h2>
                
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
                    {errors.submit}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Amount Selection */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-3">Select Amount</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[50, 100, 150, 200, 250, 300].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          className={`py-3 px-4 rounded-lg border ${
                            selectedAmount === amount && !customAmount
                              ? 'bg-[#3A98B9] text-white border-[#3A98B9]'
                              : 'bg-white text-gray-800 border-gray-200 hover:border-[#3A98B9]'
                          } transition-all`}
                          onClick={() => handleAmountClick(amount)}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Amount */}
                  <div>
                    <label htmlFor="custom-amount" className="block text-gray-700 mb-2">Or Enter Custom Amount:</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">$</span>
                      <input
                        type="number"
                        id="custom-amount"
                        min="25"
                        max="1000"
                        step="5"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        placeholder="Enter amount"
                        className="pl-8 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A98B9]/20"
                      />
                    </div>
                    {errors.amount && (
                      <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                    )}
                  </div>
                  
                  {/* Recipient & Message */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="recipient-name" className="block text-gray-700 mb-2">Recipient's Name (Optional):</label>
                      <input
                        type="text"
                        id="recipient-name"
                        value={recipientName}
                        onChange={(e) => {
                          setRecipientName(e.target.value);
                          setErrors({...errors, recipientName: null});
                        }}
                        placeholder="Who is this gift for?"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A98B9]/20"
                      />
                      {errors.recipientName && (
                        <p className="text-red-500 text-sm mt-1">{errors.recipientName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="recipient-email" className="block text-gray-700 mb-2">Recipient's Email (Optional):</label>
                      <input
                        type="email"
                        id="recipient-email"
                        value={recipientEmail}
                        onChange={(e) => {
                          setRecipientEmail(e.target.value);
                          setErrors({...errors, recipientEmail: null});
                        }}
                        placeholder="Where to send the gift card"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A98B9]/20"
                      />
                      {errors.recipientEmail && (
                        <p className="text-red-500 text-sm mt-1">{errors.recipientEmail}</p>
                      )}
                    </div>
                    
                    {!auth.currentUser && (
                      <div>
                        <label htmlFor="sender-name" className="block text-gray-700 mb-2">Your Name:</label>
                        <input
                          type="text"
                          id="sender-name"
                          value={senderName}
                          onChange={(e) => {
                            setSenderName(e.target.value);
                            setErrors({...errors, senderName: null});
                          }}
                          placeholder="From who?"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A98B9]/20"
                        />
                        {errors.senderName && (
                          <p className="text-red-500 text-sm mt-1">{errors.senderName}</p>
                        )}
                      </div>
                    )}
                    
                    <div className={auth.currentUser ? "md:col-span-2" : ""}>
                      <label htmlFor="gift-message" className="block text-gray-700 mb-2">Personal Message (Optional):</label>
                      <input
                        type="text"
                        id="gift-message"
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        placeholder="Add a personal touch"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A98B9]/20"
                      />
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </form>
              </>
            ) : (
              // Use the fixed GiftCardCheckout component
              <GiftCardCheckout 
                giftCardDetails={giftCardData}
                onBack={handleBackFromPayment}
                onSuccess={handlePaymentSuccess}
              />
            )}
          </div>
          
          {/* Only show features and FAQ in form step */}
          {checkoutStep === 'form' && (
            <>
              {/* Features Section */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Why Our Gift Cards?</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="w-12 h-12 bg-[#3A98B9]/10 rounded-full flex items-center justify-center mb-4">
                      <Gift className="h-6 w-6 text-[#3A98B9]" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Customizable</h3>
                    <p className="text-gray-600">
                      Choose any amount starting at $25 and add your personal message.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="w-12 h-12 bg-[#3A98B9]/10 rounded-full flex items-center justify-center mb-4">
                      <Calendar className="h-6 w-6 text-[#3A98B9]" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Valid for 1 Year</h3>
                    <p className="text-gray-600">
                      Plenty of time to schedule the perfect detailing experience.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="w-12 h-12 bg-[#3A98B9]/10 rounded-full flex items-center justify-center mb-4">
                      <CreditCard className="h-6 w-6 text-[#3A98B9]" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Any Service</h3>
                    <p className="text-gray-600">
                      Can be applied to any detailing service, package, or add-on.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* FAQ Section */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
                
                <div className="space-y-4">
                  <div className="border-b border-gray-100 pb-4">
                    <h3 className="font-semibold text-lg mb-1">How do I redeem a gift card?</h3>
                    <p className="text-gray-600">
                      During the checkout process, you'll have the option to enter a gift card code. The amount will be automatically applied to your total.
                    </p>
                  </div>
                  
                  <div className="border-b border-gray-100 pb-4">
                    <h3 className="font-semibold text-lg mb-1">Do gift cards expire?</h3>
                    <p className="text-gray-600">
                      Our gift cards are valid for one year from the date of purchase.
                    </p>
                  </div>
                  
                  <div className="border-b border-gray-100 pb-4">
                    <h3 className="font-semibold text-lg mb-1">Can I get a refund for a gift card?</h3>
                    <p className="text-gray-600">
                      Gift cards are non-refundable, but they are transferable to another person.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-lg mb-1">What if I lose my gift card?</h3>
                    <p className="text-gray-600">
                      If you purchased the gift card while logged in, you can find the code in your account. Otherwise, contact our support team with your purchase information.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCardsPage;